import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import { API_URL } from '../../../environment';
import Swal from "sweetalert2";

interface Time {
  id: number;
  nome: string;
  // 🔴 `fone` e `ins` são varchar e podem trazer VÁRIOS números separados por vírgula
  // ('29, 30' / '554137952654, 554130670149'). Nunca renderizar o valor cru.
  fone?: string;
  ins?: string;
  fone_evo?: string;
  ins_evo?: string;
  api_oficial?: boolean;
}

interface User {
  id: number;
  nome: string;
  email?: string;
  admin_time?: boolean;
}

// Status de conexão WhatsApp por time
interface WaStatus {
  connectionStatus: string;
  loading: boolean;
  qrCode: string | null;
  message: string;
  showRetry: boolean;
}

const TeamUserManagement = () => {
  const isAdminMaster = localStorage.getItem("isAdminMaster") === "true";
  const isTeamAdmin = localStorage.getItem("isAdmin") === "true";
  const canManageOwnTeamWa = !isAdminMaster && isTeamAdmin;

  const [times, setTimes] = useState<Time[]>([]);
  const [myTeam, setMyTeam] = useState<Time | null>(null);
  const [selectedTimeId, setSelectedTimeId] = useState<string>("");
  const [users, setUsers] = useState<User[]>([]);

  const [viewMode, setViewMode] = useState<'teams' | 'users'>(isAdminMaster ? 'teams' : 'users');
  const [currentTeamName, setCurrentTeamName] = useState("");

  // --- Modais ---
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrModalTeam, setQrModalTeam] = useState<Time | null>(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [movingUser, setMovingUser] = useState<User | null>(null);
  const [moveTargetTimeId, setMoveTargetTimeId] = useState("");
  const [movingSaving, setMovingSaving] = useState(false);

  // --- Estados de Edição ---
  const [editingTime, setEditingTime] = useState<Time | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // --- Campos Time ---
  const [nomeTime, setNomeTime] = useState("");
  const [foneTime, setFoneTime] = useState("");
  const [instanciaTime, setInstanciaTime] = useState("");

  // --- Campos Usuário ---
  const [nomeUser, setNomeUser] = useState("");
  const [emailUser, setEmailUser] = useState("");
  const [senhaUser, setSenhaUser] = useState("");
  const [isAdminTime, setIsAdminTime] = useState(false);

  // --- WhatsApp Status por Time ---
  const [waStatuses, setWaStatuses] = useState<Record<number, WaStatus>>({});
  const monitoringRef = useRef<Record<number, boolean>>({});
  const statusIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const defaultWaStatus: WaStatus = { connectionStatus: "unknown", loading: false, qrCode: null, message: "", showRetry: false };

  // Helper para atualizar status de um time específico
  const updateWaStatus = (timeId: number, partial: Partial<WaStatus>) => {
    setWaStatuses(prev => ({
      ...prev,
      [timeId]: { ...(prev[timeId] || defaultWaStatus), ...partial },
    }));
  };

  // Atualiza dados de um time tanto na lista (master) quanto no myTeam (admin de time)
  const updateTimeData = (timeId: number, partial: Partial<Time>) => {
    setTimes(prev => prev.map(t => t.id === timeId ? { ...t, ...partial } : t));
    setMyTeam(prev => prev && prev.id === timeId ? { ...prev, ...partial } : prev);
  };

  // Retorna o ins_evo válido ou null
  const getInstanceName = (time: Time): string | null => {
    const ins = time.ins_evo;
    if (!ins || String(ins) === "0" || String(ins) === "-1") return null;
    return String(ins);
  };

  // Lista de números/instâncias de um campo multi-valor ('29, 30' → ['29','30']).
  const numerosDoTime = (valor?: string): string[] =>
    String(valor ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s && s !== "0" && s !== "-1");

  // 🔴 Quem manda é `cliente_time.api_oficial` (bool). Deduzir por "tem `ins`" erra:
  // o time 120 (santolegado) tem ins/fone preenchidos e api_oficial=false — pela
  // dedução ele perderia o QR da uazapi e ficaria sem canal nenhum.
  // O fallback pelo `ins` só existe para resposta antiga, sem o campo.
  // Em time oficial o canal não oficial (uazapi/QR) NÃO é oferecido: quem conecta e
  // mantém o número é a Meta, e ler um QR aqui criaria um segundo número no mesmo
  // time — os dois canais brigando pelo mesmo lead.
  const isOficial = (time: Time): boolean => {
    if (typeof time.api_oficial === "boolean") return time.api_oficial;
    return numerosDoTime(time.ins).length > 0;
  };

  // Verifica status de UM time via API
  const checkTeamStatus = async (time: Time) => {
    // Time oficial não tem status na uazapi: é sempre "conectado", pelo número da Meta.
    if (isOficial(time)) {
      updateWaStatus(time.id, {
        connectionStatus: "official", loading: false, qrCode: null, message: "", showRetry: false,
      });
      return;
    }
    const ins = getInstanceName(time);
    if (!ins) {
      // Sem instância: marca como "no_instance"
      updateWaStatus(time.id, { connectionStatus: "no_instance" });
      return;
    }
    try {
      console.log(`[WA] Verificando time "${time.nome}" (ins_evo=${ins})...`);
      const res = await fetch(`${API_URL}/pegarinstancias`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instanceName: ins }),
      });
      if (res.ok) {
        const data = await res.json();
        console.log(`[WA] Resposta time "${time.nome}":`, data);
        const currentStatus = data[0]?.connectionStatus || "disconnected";
        updateWaStatus(time.id, { connectionStatus: currentStatus });
      } else {
        updateWaStatus(time.id, { connectionStatus: "disconnected" });
      }
    } catch (err) {
      console.error(`[WA] Erro ao verificar time "${time.nome}":`, err);
      updateWaStatus(time.id, { connectionStatus: "disconnected" });
    }
  };

  // Verifica TODOS os times (chamado ao carregar e no polling)
  const checkAllTeamStatuses = async (teamList: Time[]) => {
    console.log(`[WA] Verificando ${teamList.length} times...`);
    for (const time of teamList) {
      await checkTeamStatus(time);
    }
  };

  // Cria nova instância na Uazapi e devolve o nome usado (string numérica >= 50)
  const createNewInstance = async (time: Time): Promise<string | null> => {
    try {
      // 1. Pede o próximo nome livre (gap-fill começando em 50)
      const respNext = await fetch(`${API_URL}/proxima_instancia_disponivel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!respNext.ok) {
        console.error("Falha ao consultar próxima instância:", await respNext.text());
        return null;
      }
      const dataNext = await respNext.json();
      const newName: string | undefined = dataNext.instanceName;
      if (!newName) return null;

      // 2. Cria a instância (backend faz init + webhook + connect e devolve QR)
      const respCreate = await fetch(`${API_URL}/criarinstancia`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instanceName: newName, id_cliente: time.id }),
      });

      const dataCreate = await respCreate.json().catch(() => ({}));

      // 3. Trata 409 com retry curto se outro processo segurou o lock
      if (respCreate.status === 409 && dataCreate?.retry) {
        await new Promise(r => setTimeout(r, 2000));
        const retry = await fetch(`${API_URL}/criarinstancia`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ instanceName: newName, id_cliente: time.id }),
        });
        const retryData = await retry.json().catch(() => ({}));
        if (!retry.ok) {
          console.error("Retry de /criarinstancia falhou:", retryData);
          return null;
        }
        const rawQr = retryData.base64 || retryData.qrcode?.base64 || retryData.code || (retryData.results && retryData.results[0]?.qrcode);
        if (rawQr) {
          const formattedQr = String(rawQr).startsWith('data:') ? rawQr : `data:image/png;base64,${rawQr}`;
          updateWaStatus(time.id, { qrCode: formattedQr, message: "Escaneie para conectar", loading: false });
        }
        return newName;
      }

      if (!respCreate.ok) {
        console.error("Falha ao criar instância:", dataCreate);
        return null;
      }

      // 4. Resposta de sucesso vem do /instance/connect — mesmo formato do reconectar
      const rawQr = dataCreate.base64 || dataCreate.qrcode?.base64 || dataCreate.code || (dataCreate.results && dataCreate.results[0]?.qrcode);
      if (rawQr) {
        const formattedQr = String(rawQr).startsWith('data:') ? rawQr : `data:image/png;base64,${rawQr}`;
        updateWaStatus(time.id, { qrCode: formattedQr, message: "Escaneie para conectar", loading: false });
      } else {
        updateWaStatus(time.id, { message: "Aguardando QR Code...", loading: false });
      }
      return newName;
    } catch (err) {
      console.error("Erro em createNewInstance:", err);
      return null;
    }
  };

  // Revalida no banco se o time já tem instância — útil quando o estado local está desatualizado
  const fetchInstanceFromDb = async (timeId: number): Promise<string | null> => {
    try {
      const resp = await fetch(`${API_URL}/dados_instancia_time`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente: timeId }),
      });
      if (!resp.ok) return null;
      const data = await resp.json().catch(() => ({}));
      const insBanco = data?.ins_evo == null ? "" : String(data.ins_evo).trim();
      if (!insBanco || insBanco === "0" || insBanco === "-1") return null;
      return insBanco;
    } catch (e) {
      console.error("[WA] Falha ao consultar /dados_instancia_time:", e);
      return null;
    }
  };

  // Trigger reconexão e gerar QR Code (usa ins_evo = API não oficial / WhatsApp Web)
  const triggerTeamReconnection = async (time: Time) => {
    // Defesa: a UI já não mostra o botão em time oficial, mas nunca criar instância
    // uazapi num time que já tem número da Meta.
    if (isOficial(time)) return;

    let ins = getInstanceName(time);
    let timeRef = time;

    updateWaStatus(time.id, { loading: true, qrCode: null, message: ins ? "Iniciando conexão..." : "Verificando instância...", showRetry: false });
    setQrModalTeam(time);
    setShowQrModal(true);

    // Antes de decidir criar, revalida com o banco (estado local pode estar desatualizado)
    if (!ins) {
      const insBanco = await fetchInstanceFromDb(time.id);
      if (insBanco) {
        ins = insBanco;
        timeRef = { ...time, ins_evo: insBanco };
        updateTimeData(time.id, { ins_evo: insBanco });
        updateWaStatus(time.id, { message: "Reconectando instância existente..." });
      }
    }

    // Caso: time SEM instância (nem local, nem banco) → cria nova na Uazapi (gap-fill >= 50)
    if (!ins) {
      updateWaStatus(time.id, { message: "Criando instância na Uazapi..." });
      const newName = await createNewInstance(time);
      if (!newName) {
        updateWaStatus(time.id, { message: "Falha ao criar instância na Uazapi.", showRetry: true, loading: false });
        return;
      }
      const updatedTime: Time = { ...time, ins_evo: newName };
      updateTimeData(time.id, { ins_evo: newName });
      updateWaStatus(time.id, { connectionStatus: "connecting" });
      monitorTeamConnection(updatedTime);
      refreshAfterConnection();
      return;
    }

    // Caso: time COM instância (do estado local OU do banco) → reconecta
    try {
      const res = await fetch(`${API_URL}/reconectarinstancia`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instanceName: ins }),
      });

      const data = await res.json().catch(() => ({}));

      // Se a instância sumiu da Uazapi (404/sem token), cai pra criar nova como fallback
      if (!res.ok) {
        console.warn(`[WA] Reconexão falhou para "${ins}" (${res.status}). Tentando criar nova.`, data);
        const newName = await createNewInstance(time);
        if (!newName) {
          updateWaStatus(time.id, { message: "Instância no banco não existe mais e não foi possível criar nova.", showRetry: true, loading: false });
          return;
        }
        const updatedTime: Time = { ...time, ins_evo: newName };
        updateTimeData(time.id, { ins_evo: newName });
        updateWaStatus(time.id, { connectionStatus: "connecting" });
        monitorTeamConnection(updatedTime);
        refreshAfterConnection();
        return;
      }

      const rawQr = data.base64 || data.qrcode?.base64 || data.code || (data.results && data.results[0]?.qrcode);

      if (rawQr) {
        const formattedQr = String(rawQr).startsWith('data:') ? rawQr : `data:image/png;base64,${rawQr}`;
        updateWaStatus(time.id, { qrCode: formattedQr, message: "Escaneie para conectar", loading: false });
      } else {
        updateWaStatus(time.id, { message: "Aguardando QR Code...", loading: false });
      }

      monitorTeamConnection(timeRef);
    } catch (err) {
      console.error(`Erro na reconexão do time ${time.nome}:`, err);
      updateWaStatus(time.id, { message: "Falha ao gerar conexão.", showRetry: true, loading: false });
    }
  };

  // Desconecta (logout) o número WhatsApp atualmente conectado ao time.
  // A instância permanece, então é possível reconectar lendo um novo QR depois.
  const disconnectTeam = async (time: Time) => {
    const ins = getInstanceName(time);
    if (!ins) return;

    const confirm = await Swal.fire({
      title: "Desconectar WhatsApp?",
      text: `O número conectado a "${time.nome}" será desconectado. Você poderá reconectar depois lendo um novo QR Code.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, desconectar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });
    if (!confirm.isConfirmed) return;

    monitoringRef.current[time.id] = false;
    updateWaStatus(time.id, { loading: true });
    try {
      const res = await fetch(`${API_URL}/desconectarinstancia`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instanceName: ins }),
      });
      if (res.ok) {
        updateWaStatus(time.id, { connectionStatus: "disconnected", qrCode: null, message: "", loading: false, showRetry: false });
        Swal.fire({ icon: "success", title: "WhatsApp desconectado", timer: 1800, showConfirmButton: false });
      } else {
        updateWaStatus(time.id, { loading: false });
        Swal.fire({ icon: "error", title: "Não foi possível desconectar", text: "Tente novamente." });
      }
    } catch (err) {
      console.error(`Erro ao desconectar o time ${time.nome}:`, err);
      updateWaStatus(time.id, { loading: false });
      Swal.fire({ icon: "error", title: "Erro ao desconectar", text: "Tente novamente." });
    }
  };

  // Monitorar conexão de um time (polling) — usa ins_evo
  const monitorTeamConnection = async (time: Time) => {
    const ins = getInstanceName(time);
    if (!ins) return;
    monitoringRef.current[time.id] = true;

    let attempts = 0;
    const maxAttempts = 30;

    while (attempts < maxAttempts && monitoringRef.current[time.id]) {
      try {
        const res = await fetch(`${API_URL}/pegarinstancias`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ instanceName: ins }),
        });

        if (res.ok) {
          const data = await res.json();
          const inst = data[0];

          // Captura QR Code tardio via polling
          if (inst?.qrCode) {
            const rawQr = inst.qrCode;
            const formattedQr = typeof rawQr === 'string' && rawQr.startsWith('data:')
              ? rawQr
              : `data:image/png;base64,${typeof rawQr === 'object' ? rawQr.base64 : rawQr}`;

            setWaStatuses(prev => {
              const current = prev[time.id];
              if (current && !current.qrCode) {
                return { ...prev, [time.id]: { ...current, qrCode: formattedQr, message: "Escaneie para conectar" } };
              }
              return prev;
            });
          }

          if (inst?.connectionStatus === "open" || inst?.connectionStatus === "connected") {
            updateWaStatus(time.id, { qrCode: null, message: "Conectado!", connectionStatus: "open", loading: false, showRetry: false });
            monitoringRef.current[time.id] = false;

            // Salva fone/token/ins no banco (cliente_time + login_cliente)
            try {
              const respReg = await fetch(`${API_URL}/registrar_conexao_instancia`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ instanceName: ins, id_cliente: time.id }),
              });
              const dataReg = await respReg.json().catch(() => ({}));
              if (respReg.ok && dataReg?.ok) {
                console.log(`[WA] Conexão registrada: ins=${dataReg.instanceName} fone=${dataReg.fone}`);
                // Atualiza time local com fone_evo recém-descoberto
                updateTimeData(time.id, { ins_evo: dataReg.instanceName, fone_evo: dataReg.fone });
              } else {
                console.warn("[WA] /registrar_conexao_instancia retornou sem ok:", dataReg);
              }
            } catch (e) {
              console.error("[WA] Falha ao registrar conexão no banco:", e);
            }

            // Sincroniza times com o banco em background (não bloqueia o sucesso)
            refreshAfterConnection();

            Swal.fire({ title: "Sucesso!", text: `WhatsApp do time "${time.nome}" conectado!`, icon: "success", timer: 2500 });
            setShowQrModal(false);
            return;
          }
        }
      } catch (e) {
        console.error("Erro no monitoramento:", e);
      }

      attempts++;
      await new Promise(r => setTimeout(r, 3000));
    }

    if (monitoringRef.current[time.id]) {
      updateWaStatus(time.id, { message: "O tempo de conexão expirou.", showRetry: true, loading: false });
      monitoringRef.current[time.id] = false;
    }
  };

  // 1. Busca Times (apenas se Master)
  const fetchTimes = async () => {
    const id_grupo = localStorage.getItem("id_grupo");
    if (!id_grupo) return;
    try {
      const res = await fetch(`${API_URL}/pegartodostimes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_grupo }),
      });
      if (res.ok) {
        const data = await res.json();
        console.log("[WA] pegartodostimes response:", data);
        if (Array.isArray(data)) {
          setTimes(data);
          // Verifica status de TODOS os times imediatamente
          for (const time of data) {
            checkTeamStatus(time);
          }
        }
      }
    } catch (err) {
      console.error("Erro ao buscar times:", err);
    }
  };

  // Busca dados do próprio time (admin de time, sem ser master)
  const fetchMyTeam = async () => {
    const idTime = localStorage.getItem("id_cliente");
    if (!idTime) return;
    const teamId = Number(idTime);
    let team: Time = { id: teamId, nome: "Meu Time" };
    try {
      const res = await fetch(`${API_URL}/dados_instancia_time`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente: idTime }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        // `ins`/`fone` = canal oficial (Meta); `ins_evo`/`fone_evo` = uazapi.
        // `ins`/`fone` = canal oficial (Meta); `ins_evo`/`fone_evo` = uazapi;
        // `api_oficial` = o flag que decide qual canal a tela oferece.
        team = { ...team, ins_evo: data?.ins_evo, fone_evo: data?.fone_evo,
                 ins: data?.ins, fone: data?.fone, api_oficial: data?.api_oficial };
      }
    } catch (err) {
      console.error("Erro ao buscar dados do time:", err);
    }
    setMyTeam(team);
    checkTeamStatus(team);
  };

  // Refresh contextual: master sincroniza todos os times; admin de time sincroniza só o seu
  const refreshAfterConnection = () => {
    if (isAdminMaster) {
      fetchTimes();
    } else if (canManageOwnTeamWa) {
      fetchMyTeam();
    }
  };

  useEffect(() => {
    if (isAdminMaster) {
      fetchTimes();
    } else {
      const myTime = localStorage.getItem("id_cliente");
      if (myTime) {
        setSelectedTimeId(myTime);
        setCurrentTeamName("Meu Time");
        setViewMode('users');
        if (canManageOwnTeamWa) {
          fetchMyTeam();
        }
      }
    }
  }, [isAdminMaster]);

  // Polling de status do próprio time a cada 15s (admin de time)
  useEffect(() => {
    if (!canManageOwnTeamWa || !myTeam) return;
    const id = setInterval(() => { checkTeamStatus(myTeam); }, 15000);
    return () => clearInterval(id);
  }, [canManageOwnTeamWa, myTeam?.id, myTeam?.ins_evo]);

  // Polling de status de todos os times a cada 15s
  useEffect(() => {
    if (viewMode === 'teams' && times.length > 0) {
      statusIntervalRef.current = setInterval(() => {
        for (const time of times) {
          checkTeamStatus(time);
        }
      }, 15000);
      return () => {
        if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
      };
    }
  }, [viewMode, times]);

  // Cleanup monitoring ao desmontar
  useEffect(() => {
    return () => {
      monitoringRef.current = {};
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
    };
  }, []);

  const handleTimeClick = (time: Time) => {
    setSelectedTimeId(String(time.id));
    setCurrentTeamName(time.nome);
    setViewMode('users');
  };

  const handleBackToTeams = () => {
    setViewMode('teams');
    setSelectedTimeId("");
    setUsers([]);
  };

  // 2. Busca Usuários quando Time muda
  const fetchUsers = async () => {
    if (!selectedTimeId) {
      setUsers([]);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/pegarusuariostime`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_time: selectedTimeId }),
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setUsers(data);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [selectedTimeId]);

  // --- CRUD TIME ---
  const openTimeModal = (time?: Time) => {
    if (time) {
      setEditingTime(time);
      setNomeTime(time.nome);
    } else {
      setEditingTime(null);
      setNomeTime("");
    }
    setShowTimeModal(true);
  };

  const handleSaveTime = async () => {
    const id_grupo = localStorage.getItem("id_grupo");
    if (!nomeTime || !id_grupo) return;

    try {
      if (editingTime) {
        await fetch(`${API_URL}/editar_time`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: editingTime.id,
            nome: nomeTime,
          }),
        });
      } else {
        await fetch(`${API_URL}/criar_time`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nome: nomeTime,
            id_grupo
          }),
        });
      }
      setShowTimeModal(false);
      fetchTimes();
    } catch (err) {
      console.error(err);
      alert("Erro ao salvar time");
    }
  };

  const handleDeleteTime = async (id: number) => {
    if (!window.confirm("Tem certeza que deseja excluir este time?")) return;
    try {
      await fetch(`${API_URL}/excluir_time`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      fetchTimes();
    } catch (err) {
      console.error(err);
    }
  };

  // --- CRUD USUÁRIO ---
  const openUserModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setNomeUser(user.nome);
      setEmailUser(user.email || "");
      setSenhaUser("");
      setIsAdminTime(!!user.admin_time);
    } else {
      setEditingUser(null);
      setNomeUser("");
      setEmailUser("");
      setSenhaUser("");
      setIsAdminTime(false);
    }
    setShowUserModal(true);
  };

  // As rotas de usuário respondem {"error": "..."} com a mensagem CRUA do Postgres.
  // Traduz o caso que acontece de verdade — `login_cliente.login` é UNIQUE GLOBAL,
  // atravessa tenants, então um e-mail já usado em QUALQUER grupo derruba a criação —
  // e repassa o resto. Sem isso o operador via o modal fechar como se tivesse salvo.
  const mensagemDeErro = async (res: Response, padrao: string): Promise<string> => {
    const bruto = await res.json().then((j: any) => j?.error || "").catch(() => "");
    if (/login_cliente_login_key|duplicate key/i.test(bruto)) {
      return "Este e-mail já está cadastrado em outra conta do sistema. Use outro e-mail.";
    }
    return bruto || padrao;
  };

  const handleSaveUser = async () => {
    const id_grupo = localStorage.getItem("id_grupo");
    if (!selectedTimeId) return;

    if (editingUser) {
      // Editando: só o nome é obrigatório. Email/senha em branco = manter os atuais.
      if (!nomeUser) {
        Swal.fire({ title: "Atenção", text: "O nome é obrigatório.", icon: "warning" });
        return;
      }
    } else {
      // Novo usuário: nome, email e senha são obrigatórios.
      if (!nomeUser || !emailUser || !senhaUser) {
        Swal.fire({ title: "Atenção", text: "Nome, email e senha são obrigatórios para novos usuários.", icon: "warning" });
        return;
      }
    }

    try {
      let res: Response;
      if (editingUser) {
        res = await fetch(`${API_URL}/editar_usuario`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: editingUser.id,
            nome: nomeUser,
            email: emailUser || undefined,
            senha: senhaUser || undefined,
            admin_time: isAdminTime
          }),
        });
      } else {
        res = await fetch(`${API_URL}/criar_usuario`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nome: nomeUser,
            email: emailUser,
            senha: senhaUser,
            id_time: selectedTimeId,
            id_cliente: id_grupo,
            admin_time: isAdminTime
          }),
        });
      }
      // O modal SÓ fecha se o backend confirmou. Antes, um 500 (e-mail duplicado é o
      // caso comum) era descartado: o modal fechava, a lista recarregava sem o usuário
      // e o operador ficava sem saber que nada tinha sido criado.
      if (!res.ok) {
        Swal.fire({
          title: "Erro",
          text: await mensagemDeErro(
            res,
            editingUser ? "Não foi possível salvar as alterações." : "Não foi possível criar o usuário."
          ),
          icon: "error",
        });
        return;   // mantém o modal aberto, com o que já foi digitado
      }
      setShowUserModal(false);
      fetchUsers();
    } catch (err) {
      console.error(err);
      Swal.fire({ title: "Erro", text: "Falha de conexão com o servidor.", icon: "error" });
    }
  };

  // --- TRANSFERIR LOGIN DE TIME ---
  // Só o admin master transfere: é ele quem enxerga todos os times do grupo (a lista
  // `times` só é carregada para ele) e o destino tem que ser um time do MESMO grupo.
  const openMoveModal = (user: User) => {
    setMovingUser(user);
    setMoveTargetTimeId("");
    setShowMoveModal(true);
  };

  const handleMoveUser = async () => {
    if (!movingUser || !moveTargetTimeId) return;
    const destino = times.find(t => String(t.id) === moveTargetTimeId);

    const confirma = await Swal.fire({
      title: "Mover login de time?",
      html:
        `<b>${movingUser.nome}</b> passa a fazer parte de <b>${destino?.nome || "outro time"}</b> ` +
        `e passa a enviar pelo número desse time.` +
        `<div style="margin-top:10px;font-size:13px;color:#64748b">` +
        `Os leads e conversas dele continuam no time atual — para levar a carteira junto, ` +
        `use a transferência de leads.</div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, mover",
      cancelButtonText: "Cancelar",
    });
    if (!confirma.isConfirmed) return;

    setMovingSaving(true);
    try {
      const res = await fetch(`${API_URL}/transferir_login_time`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_user: movingUser.id, novo_id_time: Number(moveTargetTimeId) }),
      });
      if (!res.ok) {
        Swal.fire({
          title: "Erro",
          text: await mensagemDeErro(res, "Não foi possível mover o login de time."),
          icon: "error",
        });
        return;   // mantém o modal aberto com o destino escolhido
      }
      setShowMoveModal(false);
      setMovingUser(null);
      fetchUsers();
      Swal.fire({
        icon: "success",
        title: "Login movido",
        text: `${movingUser.nome} agora está em "${destino?.nome || "outro time"}".`,
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({ title: "Erro", text: "Falha de conexão com o servidor.", icon: "error" });
    } finally {
      setMovingSaving(false);
    }
  };

  const handleDeleteUser = async (id: number) => {
    if (!window.confirm("Tem certeza que deseja excluir este usuário?")) return;
    try {
      const res = await fetch(`${API_URL}/excluir_usuario`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      if (!res.ok) {
        Swal.fire({
          title: "Erro",
          text: await mensagemDeErro(res, "Não foi possível excluir o usuário."),
          icon: "error",
        });
        return;
      }
      fetchUsers();
    } catch (err) {
      console.error(err);
      Swal.fire({ title: "Erro", text: "Falha de conexão com o servidor.", icon: "error" });
    }
  };

  // Helper para renderizar badge de status
  const renderStatusBadge = (time: Time) => {
    const ws = waStatuses[time.id];
    const status = ws?.connectionStatus || "unknown";

    if (status === "no_instance") {
      return (
        <span className="badge bg-secondary-transparent text-secondary rounded-pill" style={{ fontSize: "10px" }}>
          <i className="ti ti-plug-x me-1" />Sem instância
        </span>
      );
    }
    if (status === "open" || status === "connected") {
      return (
        <span className="badge bg-success-transparent text-success rounded-pill" style={{ fontSize: "10px" }}>
          <i className="ti ti-circle-check-filled me-1" />Conectado
        </span>
      );
    }
    if (status === "unknown") {
      return (
        <span className="badge bg-warning-transparent text-warning rounded-pill" style={{ fontSize: "10px" }}>
          <i className="ti ti-loader me-1" />Verificando...
        </span>
      );
    }
    return (
      <span className="badge bg-danger-transparent text-danger rounded-pill" style={{ fontSize: "10px" }}>
        <i className="ti ti-circle-x-filled me-1" />Desconectado
      </span>
    );
  };

  // Conteúdo do modal QR
  const renderQrModalContent = () => {
    if (!qrModalTeam) return null;
    const ws = waStatuses[qrModalTeam.id];
    if (!ws) return null;

    return (
      <div className="text-center">
        <div
          className="qr-code-placeholder bg-light rounded-4 d-flex align-items-center justify-content-center mx-auto mb-3"
          style={{ width: "220px", maxWidth: "90vw", height: "220px", border: "2px dashed #e2e8f0", overflow: "hidden", position: "relative" }}
        >
          {ws.qrCode ? (
            <img
              src={ws.qrCode}
              alt="WhatsApp QR Code"
              className="img-fluid"
              style={{ width: "100%", height: "100%", objectFit: "contain", padding: "10px" }}
            />
          ) : ws.loading || ws.message.includes("Iniciando") || ws.message.includes("Aguardando") ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          ) : ws.connectionStatus === "open" || ws.connectionStatus === "connected" ? (
            <div className="text-success text-center">
              <i className="ti ti-circle-check" style={{ fontSize: "60px" }}></i>
              <p className="small fw-bold mt-2">Sincronizado</p>
            </div>
          ) : (
            <div className="text-center text-muted">
              <i className="ti ti-qrcode" style={{ fontSize: "60px", opacity: 0.3 }}></i>
              <p className="small mt-2">Aguardando QR Code...</p>
            </div>
          )}
        </div>

        {ws.qrCode && !ws.showRetry && (
          <p className="small text-primary fw-bold animate__animated animate__pulse animate__infinite">
            Escaneie o QR Code com o WhatsApp
          </p>
        )}

        {ws.showRetry && (
          <div className="mt-2">
            <p className="small text-danger mb-2">Não foi possível conectar. Tente novamente.</p>
            <button
              className="btn btn-sm btn-outline-danger rounded-pill"
              onClick={() => triggerTeamReconnection(qrModalTeam)}
            >
              <i className="ti ti-refresh me-1" /> Tentar novamente
            </button>
          </div>
        )}

        {qrModalTeam.fone_evo && (
          <p className="small text-muted mt-2">
            <i className="ti ti-phone me-1" />{qrModalTeam.fone_evo}
          </p>
        )}
      </div>
    );
  };

  // Helper: iniciais do nome
  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  // Helper: cor do avatar baseada no index
  const getAvatarClass = (index: number) => `avatar-${(index % 6) + 1}`;

  // Helper: status dot/label classes
  const getStatusClasses = (status: string) => {
    if (status === "official" || status === "open" || status === "connected") return { dot: "dot-connected", label: "label-connected", text: "Conectado" };
    if (status === "no_instance") return { dot: "dot-no-instance", label: "label-no-instance", text: "Sem instância" };
    if (status === "unknown") return { dot: "dot-unknown", label: "label-unknown", text: "Verificando..." };
    return { dot: "dot-disconnected", label: "label-disconnected", text: "Desconectado" };
  };

  // Count connected teams (time oficial conta como conectado — o número é da Meta)
  const connectedCount = times.filter(t => {
    const s = waStatuses[t.id]?.connectionStatus;
    return s === "official" || s === "open" || s === "connected";
  }).length;

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="tm-page-header d-flex justify-content-between align-items-center">
          <div className="tm-page-title">
            <h4>Gerenciamento de Equipe</h4>
            <p>
              {viewMode === 'teams' ? "Gerencie seus times e conexões WhatsApp" : `Gerenciando usuários de: ${currentTeamName}`}
            </p>
          </div>
          <div className="tm-header-actions">
            {viewMode === 'users' && isAdminMaster && (
              <button className="tm-btn-back" onClick={handleBackToTeams}>
                <i className="ti ti-arrow-left" /> Voltar para Times
              </button>
            )}
            {viewMode === 'teams' && isAdminMaster && (
              <button className="tm-btn-new" onClick={() => openTimeModal()}>
                <i className="ti ti-plus" /> Novo Time
              </button>
            )}
          </div>
        </div>

        {/* Stats Bar */}
        {viewMode === 'teams' && times.length > 0 && (
          <div className="tm-stats-bar">
            <div className="tm-stat-pill">
              <div className="tm-stat-icon icon-teams"><i className="ti ti-users-group" /></div>
              <div className="tm-stat-text">
                <div className="tm-stat-value">{times.length}</div>
                <div className="tm-stat-label">Times</div>
              </div>
            </div>
            <div className="tm-stat-pill">
              <div className="tm-stat-icon icon-online"><i className="ti ti-wifi" /></div>
              <div className="tm-stat-text">
                <div className="tm-stat-value">{connectedCount}</div>
                <div className="tm-stat-label">Online</div>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'users' && (
          <div className="tm-stats-bar">
            <div className="tm-stat-pill">
              <div className="tm-stat-icon icon-users"><i className="ti ti-users" /></div>
              <div className="tm-stat-text">
                <div className="tm-stat-value">{users.length}</div>
                <div className="tm-stat-label">Usuários</div>
              </div>
            </div>
          </div>
        )}

        {/* MODO: TIMES */}
        {viewMode === 'teams' && (
          <div className="row">
            {times.map((time) => {
              const ws = waStatuses[time.id];
              const oficial = isOficial(time);
              const fonesOficiais = numerosDoTime(time.fone);
              const status = oficial ? "official" : (ws?.connectionStatus || "unknown");
              const isConnected = status === "open" || status === "connected";
              const sc = getStatusClasses(status);

              return (
                <div key={time.id} className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4">
                  <div className="tm-team-card" onClick={() => handleTimeClick(time)}>
                    {/* Gradient Header */}
                    <div className="tm-team-header">
                      <div className="tm-team-name">
                        <span className="tm-team-icon"><i className="ti ti-users-group" /></span>
                        {time.nome}
                      </div>
                      <div className="tm-team-actions" onClick={e => e.stopPropagation()}>
                        <button className="tm-action-btn" onClick={() => openTimeModal(time)}>
                          <i className="ti ti-edit" />
                        </button>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="tm-team-body">
                      {/* Time oficial mostra o número da Meta (cliente_time.fone);
                          os demais, o número lido pelo QR (fone_evo). */}
                      {(oficial ? fonesOficiais.length > 0 : !!time.fone_evo) && (
                        <div className="tm-team-phone">
                          <i className="ti ti-phone" />
                          {oficial ? fonesOficiais[0] : time.fone_evo}
                          {/* Time multi-número: mostra o primeiro e conta o resto,
                              senão sairia a lista crua "5541..., 5541...". */}
                          {oficial && fonesOficiais.length > 1 && (
                            <span title={fonesOficiais.join(", ")}>+{fonesOficiais.length - 1}</span>
                          )}
                          {oficial && <span className="tm-badge-oficial">API Oficial</span>}
                        </div>
                      )}

                      {/* Status Row */}
                      <div className="tm-status-row" onClick={e => e.stopPropagation()}>
                        <div className="tm-status-indicator">
                          <span className={`tm-status-dot ${sc.dot}`} />
                          <span className={`tm-status-label ${sc.label}`}>{sc.text}</span>
                        </div>

                        {oficial ? (
                          // Sem QR, sem reconectar e sem desconectar: o número é da Meta.
                          <span className="tm-online-badge">
                            <i className="ti ti-wifi" />Online
                          </span>
                        ) : (
                          <>
                            {ws?.connectionStatus === "no_instance" && (
                              <button
                                className="tm-connect-btn btn-connect"
                                onClick={() => triggerTeamReconnection(time)}
                                disabled={ws?.loading}
                              >
                                <i className="ti ti-plug me-1" />Conectar
                              </button>
                            )}

                            {getInstanceName(time) && !isConnected && ws?.connectionStatus !== "unknown" && ws?.connectionStatus !== "no_instance" && (
                              <button
                                className="tm-connect-btn btn-reconnect"
                                onClick={() => triggerTeamReconnection(time)}
                                disabled={ws?.loading}
                              >
                                <i className="ti ti-qrcode me-1" />
                                {ws?.loading ? "..." : "Reconectar"}
                              </button>
                            )}

                            {isConnected && (
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="tm-online-badge">
                                  <i className="ti ti-wifi" />Online
                                </span>
                                <button
                                  className="tm-connect-btn btn-disconnect"
                                  onClick={() => disconnectTeam(time)}
                                  disabled={ws?.loading}
                                >
                                  <i className="ti ti-plug-off me-1" />Desconectar
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="tm-team-footer">
                      <span className="tm-team-enter">
                        Ver usuários <i className="ti ti-chevron-right" />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            {times.length === 0 && (
              <div className="col-12">
                <div className="tm-empty-state">
                  <div className="tm-empty-icon"><i className="ti ti-users-group" /></div>
                  <p className="tm-empty-text">Nenhum time encontrado. Crie seu primeiro time!</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Banner WhatsApp do próprio time (admin de time, não-master) */}
        {viewMode === 'users' && canManageOwnTeamWa && myTeam && (() => {
          const ws = waStatuses[myTeam.id];
          const oficial = isOficial(myTeam);
          const status = oficial ? "official" : (ws?.connectionStatus || "unknown");
          const isConnected = status === "open" || status === "connected";
          const sc = getStatusClasses(status);
          return (
            <div className="tm-wa-banner">
              <div className="tm-wa-banner-info">
                <div className="tm-wa-banner-icon">
                  <i className="ti ti-brand-whatsapp" />
                </div>
                <div className="tm-wa-banner-text">
                  <div className="tm-wa-banner-title">
                    <span className={`tm-status-dot ${sc.dot}`} />
                    <span className="tm-wa-banner-label">WhatsApp do time:</span>
                    <span className={`tm-status-label ${sc.label}`}>{sc.text}</span>
                  </div>
                  {(oficial ? numerosDoTime(myTeam.fone).length > 0 : !!myTeam.fone_evo) && (
                    <div className="tm-wa-banner-phone">
                      <i className="ti ti-phone" />
                      {oficial ? numerosDoTime(myTeam.fone)[0] : myTeam.fone_evo}
                      {oficial && <span className="tm-badge-oficial">API Oficial</span>}
                    </div>
                  )}
                </div>
              </div>
              <div className="tm-wa-banner-actions">
                {oficial ? (
                  // Time oficial: só o indicador. Conectar/desconectar é com a Meta.
                  <span className="tm-online-badge">
                    <i className="ti ti-wifi" />Online
                  </span>
                ) : (
                  <>
                    {status === "no_instance" && (
                      <button
                        className="tm-connect-btn btn-connect"
                        onClick={() => triggerTeamReconnection(myTeam)}
                        disabled={ws?.loading}
                      >
                        <i className="ti ti-plug" />Conectar
                      </button>
                    )}
                    {getInstanceName(myTeam) && !isConnected && status !== "unknown" && status !== "no_instance" && (
                      <button
                        className="tm-connect-btn btn-reconnect"
                        onClick={() => triggerTeamReconnection(myTeam)}
                        disabled={ws?.loading}
                      >
                        <i className="ti ti-qrcode" />{ws?.loading ? "..." : "Reconectar"}
                      </button>
                    )}
                    {isConnected && (
                      <>
                        <span className="tm-online-badge">
                          <i className="ti ti-wifi" />Online
                        </span>
                        <button
                          className="tm-connect-btn btn-disconnect"
                          onClick={() => disconnectTeam(myTeam)}
                          disabled={ws?.loading}
                        >
                          <i className="ti ti-plug-off" />Desconectar
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {/* MODO: USUÁRIOS */}
        {viewMode === 'users' && (
          <div className="row">
            {/* Add Card */}
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4">
              <div className="tm-add-card" onClick={() => openUserModal()}>
                <div className="tm-add-content">
                  <div className="tm-add-icon"><i className="ti ti-plus" /></div>
                  <span className="tm-add-label">Novo Usuário</span>
                </div>
              </div>
            </div>

            {users.map((u, index) => (
              <div key={u.id} className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4">
                <div className="tm-user-card">
                  <div className="tm-user-body">
                    <div className={`tm-user-avatar ${getAvatarClass(index)}`}>
                      {getInitials(u.nome)}
                    </div>
                    <div className="tm-user-name">{u.nome}</div>
                    {u.email && <div className="tm-user-email">{u.email}</div>}
                    <div className="tm-user-badges">
                      {u.admin_time && <span className="tm-badge badge-admin"><i className="ti ti-shield-check me-1" />Admin</span>}
                      {!u.admin_time && <span className="tm-badge badge-user"><i className="ti ti-user me-1" />Membro</span>}
                      <span className="tm-badge badge-id">ID: {u.id}</span>
                    </div>
                    <div className="tm-user-actions">
                      <button className="tm-edit-btn" onClick={() => openUserModal(u)}>
                        <i className="ti ti-edit" /> Editar
                      </button>
                      {/* Mover para outro time: só faz sentido para quem enxerga os
                          outros times do grupo (admin master). */}
                      {isAdminMaster && times.length > 1 && (
                        <button className="tm-move-btn" onClick={() => openMoveModal(u)}>
                          <i className="ti ti-arrows-exchange" /> Mover
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {users.length === 0 && (
              <div className="col-12">
                <div className="tm-empty-state">
                  <div className="tm-empty-icon"><i className="ti ti-user-plus" /></div>
                  <p className="tm-empty-text">Nenhum usuário encontrado neste time.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Time */}
      <Modal show={showTimeModal} onHide={() => setShowTimeModal(false)} centered dialogClassName="tm-modal">
        <Modal.Header closeButton>
          <Modal.Title><i className="ti ti-users-group me-2" />{editingTime ? "Editar Time" : "Novo Time"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tm-form-group">
            <label className="tm-form-label">Nome do Time</label>
            <div className="tm-input-wrapper">
              <i className="ti ti-users tm-input-icon" />
              <Form.Control type="text" value={nomeTime} onChange={e => setNomeTime(e.target.value)} placeholder="Ex: Equipe Comercial" />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="tm-btn-cancel" onClick={() => setShowTimeModal(false)}>Cancelar</button>
          <button className="tm-btn-save" onClick={handleSaveTime}>Salvar</button>
        </Modal.Footer>
      </Modal>

      {/* Modal Usuário */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} centered dialogClassName="tm-modal">
        <Modal.Header closeButton>
          <Modal.Title><i className="ti ti-user-plus me-2" />{editingUser ? "Editar Usuário" : "Novo Usuário"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tm-form-group">
            <label className="tm-form-label">Nome</label>
            <div className="tm-input-wrapper">
              <i className="ti ti-user tm-input-icon" />
              <Form.Control type="text" value={nomeUser} onChange={e => setNomeUser(e.target.value)} placeholder="Nome completo" />
            </div>
          </div>
          <div className="tm-form-group">
            <label className="tm-form-label">Email (Login)</label>
            <div className="tm-input-wrapper">
              <i className="ti ti-mail tm-input-icon" />
              <Form.Control type="email" value={emailUser} onChange={e => setEmailUser(e.target.value)} placeholder="email@empresa.com" />
            </div>
          </div>
          <div className="tm-form-group">
            <label className="tm-form-label">Senha {editingUser && "(deixe vazio para manter)"}</label>
            <div className="tm-input-wrapper">
              <i className="ti ti-lock tm-input-icon" />
              <Form.Control type="password" value={senhaUser} onChange={e => setSenhaUser(e.target.value)} placeholder="••••••••" />
            </div>
          </div>
          <div className="tm-checkbox">
            <Form.Check
              type="checkbox"
              id="admin-check"
              label="Admin do Time"
              checked={isAdminTime}
              onChange={e => setIsAdminTime(e.target.checked)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="tm-btn-cancel" onClick={() => setShowUserModal(false)}>Cancelar</button>
          <button className="tm-btn-save" onClick={handleSaveUser}>Salvar</button>
        </Modal.Footer>
      </Modal>

      {/* Modal Mover Login de Time */}
      <Modal show={showMoveModal} onHide={() => setShowMoveModal(false)} centered dialogClassName="tm-modal">
        <Modal.Header closeButton>
          <Modal.Title><i className="ti ti-arrows-exchange me-2" />Mover login de time</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tm-form-group">
            <label className="tm-form-label">Usuário</label>
            <div className="tm-move-user">
              <i className="ti ti-user" />
              <div>
                <div className="tm-move-user-name">{movingUser?.nome}</div>
                {movingUser?.email && <div className="tm-move-user-email">{movingUser.email}</div>}
              </div>
            </div>
          </div>
          <div className="tm-form-group">
            <label className="tm-form-label">Time de destino</label>
            <div className="tm-input-wrapper">
              <i className="ti ti-users-group tm-input-icon" />
              <Form.Select value={moveTargetTimeId} onChange={e => setMoveTargetTimeId(e.target.value)}>
                <option value="">Selecione o time…</option>
                {times
                  .filter(t => String(t.id) !== String(selectedTimeId))
                  .map(t => (
                    <option key={t.id} value={String(t.id)}>
                      {t.nome}{isOficial(t) ? " — API Oficial" : ""}
                    </option>
                  ))}
              </Form.Select>
            </div>
          </div>
          <div className="tm-move-warning">
            <i className="ti ti-info-circle" />
            <span>
              O login passa a usar o número do time de destino. Os leads e conversas
              continuam no time atual.
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="tm-btn-cancel" onClick={() => setShowMoveModal(false)}>Cancelar</button>
          <button className="tm-btn-save" onClick={handleMoveUser} disabled={!moveTargetTimeId || movingSaving}>
            {movingSaving ? "Movendo…" : "Mover"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Modal QR Code WhatsApp */}
      <Modal show={showQrModal} onHide={() => { setShowQrModal(false); if (qrModalTeam) monitoringRef.current[qrModalTeam.id] = false; }} centered dialogClassName="tm-qr-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="ti ti-brand-whatsapp me-2" />
            Conectar WhatsApp — {qrModalTeam?.nome}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {qrModalTeam && (() => {
            const ws = waStatuses[qrModalTeam.id];
            if (!ws) return null;
            return (
              <div className="tm-qr-container">
                <div className="tm-qr-box">
                  {ws.qrCode ? (
                    <img src={ws.qrCode} alt="WhatsApp QR Code" />
                  ) : ws.loading || ws.message.includes("Iniciando") || ws.message.includes("Aguardando") ? (
                    <div className="tm-qr-loading">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    </div>
                  ) : ws.connectionStatus === "open" || ws.connectionStatus === "connected" ? (
                    <div className="tm-qr-success">
                      <i className="ti ti-circle-check" />
                      <p>Sincronizado</p>
                    </div>
                  ) : (
                    <div className="tm-qr-placeholder">
                      <i className="ti ti-qrcode" />
                      <p>Aguardando QR Code...</p>
                    </div>
                  )}
                </div>

                {ws.qrCode && !ws.showRetry && (
                  <p className="tm-qr-scanning">Escaneie o QR Code com o WhatsApp</p>
                )}

                {ws.showRetry && (
                  <div className="tm-qr-retry">
                    <p>Não foi possível conectar. Tente novamente.</p>
                    <button onClick={() => triggerTeamReconnection(qrModalTeam)}>
                      <i className="ti ti-refresh me-1" /> Tentar novamente
                    </button>
                  </div>
                )}

                {qrModalTeam.fone_evo && (
                  <p className="tm-qr-phone">
                    <i className="ti ti-phone" />{qrModalTeam.fone_evo}
                  </p>
                )}
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <button className="tm-btn-cancel" onClick={() => { setShowQrModal(false); if (qrModalTeam) monitoringRef.current[qrModalTeam.id] = false; }}>
            Fechar
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TeamUserManagement;
